/**
 * Account dialogs (design/exports/account/web-6a, web-6b, web-6c): SIGN OUT? confirm, DELETE ACCOUNT? (type DELETE
 * to confirm) and ACCOUNT DELETED (PLAY OFFLINE / NEW ACCOUNT). Opened from Settings → ACCOUNT via `accountDialog`.
 */
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { LAYER, useFocusGrid } from '../nav';
import '../screens/settings.css';
import { deleteAccountWeb, signOutWeb, walletBalance, webPlayingOffline } from '../wallet';

export type AccountDialog = 'signout' | 'delete' | 'deleted';
export const accountDialog = signal<AccountDialog | null>(null);
const close = (): void => {
  accountDialog.value = null;
};

export function AccountDialogs() {
  const d = accountDialog.value;
  if (!d) return null;
  return (
    <div class="hf-gate hf-gate--dim" role="presentation">
      {d === 'signout' ? <SignOutConfirm /> : d === 'delete' ? <DeleteConfirm /> : <Deleted />}
    </div>
  );
}

function SignOutConfirm() {
  const [busy, setBusy] = useState(false);
  const go = (): void => {
    if (busy) return;
    setBusy(true);
    void signOutWeb().finally(close);
  };
  const grid = useFocusGrid({
    count: 2,
    cols: 2,
    horizontal: true,
    initial: 0,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: (i) => (i === 0 ? close() : go()),
    onBack: close,
  });
  return (
    <div class="hf-dlg hf-dlg--sm" role="dialog" aria-modal="true" aria-label="Sign out" data-testid="signout-dialog">
      <div class="hf-dlg__badge hf-dlg__badge--purple" aria-hidden="true">
        ⇥
      </div>
      <h2 class="t-display hf-dlg__title hf-dlg__title--white">SIGN OUT?</h2>
      <p class="hf-dlg__body">
        Your coins, heads and Elo are saved to your account. Sign back in any time to pick up where you left off.
      </p>
      <div class="hf-dlg__actions">
        <button type="button" class="hf-btn hf-btn--outline interactive" {...grid.item(0)}>
          CANCEL
        </button>
        <button
          type="button"
          class="hf-btn hf-btn--purple interactive"
          data-testid="signout-confirm"
          disabled={busy}
          {...grid.item(1)}
        >
          {busy ? 'SIGNING OUT…' : 'SIGN OUT'}
        </button>
      </div>
    </div>
  );
}

function DeleteConfirm() {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const ready = text.trim().toUpperCase() === 'DELETE';
  useEffect(() => input.current?.focus(), []);
  const go = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setErr(null);
    void deleteAccountWeb().then((r) => {
      setBusy(false);
      if (r === 'ok') accountDialog.value = 'deleted';
      else setErr(r === 'offline' ? 'No connection — nothing was deleted. Try again.' : 'Could not delete the account — try again.');
    });
  };
  // Typing goes to the input; Esc / B cancels. The buttons work with mouse / tap and Enter in the field.
  const grid = useFocusGrid({
    count: 2,
    cols: 2,
    horizontal: true,
    active: true,
    priority: LAYER.MODAL,
    isDisabled: (i) => i === 1 && !ready,
    onConfirm: (i) => (i === 0 ? close() : go()),
    onBack: close,
  });
  const coins = walletBalance.value;
  return (
    <div class="hf-dlg hf-dlg--md" role="alertdialog" aria-modal="true" aria-label="Delete account" data-testid="delete-dialog">
      <h2 class="t-display hf-dlg__title hf-dlg__title--red">DELETE ACCOUNT?</h2>
      <p class="hf-dlg__body">This permanently erases your account. It can't be undone and purchases aren't refundable.</p>
      <ul class="hf-dlg__loss">
        <li>🪙 {coins.toLocaleString()} coins (incl. purchased coins)</li>
        <li>🔓 Unlocked heads &amp; balls</li>
        <li>🏆 Elo, rank &amp; match history</li>
      </ul>
      <label class="hf-dlg__label" for="hf-delete-confirm">
        Type DELETE to confirm
      </label>
      <input
        id="hf-delete-confirm"
        ref={input}
        class="hf-dlg__input"
        data-testid="delete-input"
        autocomplete="off"
        spellcheck={false}
        placeholder="DELETE"
        value={text}
        onInput={(e) => setText((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') go();
          if (e.key === 'Escape') close();
        }}
      />
      {err && (
        <p class="hf-dlg__err" role="status">
          {err}
        </p>
      )}
      <div class="hf-dlg__actions">
        <button type="button" class="hf-btn hf-btn--outline interactive" {...grid.item(0)}>
          KEEP ACCOUNT
        </button>
        <button
          type="button"
          class="hf-btn hf-btn--red interactive"
          data-testid="delete-confirm"
          disabled={!ready || busy}
          {...grid.item(1)}
        >
          {busy ? 'DELETING…' : '🗑 DELETE FOREVER'}
        </button>
      </div>
    </div>
  );
}

function Deleted() {
  const offline = (): void => {
    webPlayingOffline.value = true;
    close();
  };
  const fresh = (): void => {
    webPlayingOffline.value = false; // back to the sign-in gate
    close();
  };
  const grid = useFocusGrid({
    count: 2,
    cols: 2,
    horizontal: true,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: (i) => (i === 0 ? offline() : fresh()),
  });
  return (
    <div class="hf-dlg hf-dlg--sm" role="dialog" aria-modal="true" aria-label="Account deleted" data-testid="deleted-dialog">
      <div class="hf-dlg__badge hf-dlg__badge--green" aria-hidden="true">
        ✓
      </div>
      <h2 class="t-display hf-dlg__title hf-dlg__title--white">ACCOUNT DELETED</h2>
      <p class="hf-dlg__body">Your data has been erased. Thanks for playing Hand Egg.</p>
      <div class="hf-dlg__actions">
        <button type="button" class="hf-btn hf-btn--gold interactive" {...grid.item(0)}>
          PLAY OFFLINE
        </button>
        <button type="button" class="hf-btn hf-btn--outline interactive" data-testid="deleted-new" {...grid.item(1)}>
          NEW ACCOUNT
        </button>
      </div>
    </div>
  );
}
